import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { Language } from '../entities/language';
import { ImageColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/image-column/image-column.component';
import { PrimitiveColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/primitive-column/primitive-column/primitive-column.component';
import { ObjectColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/object-column/object-column.component';
import { MultilangueColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/multilangue-column/multilangue-column.component';
import { PictoColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/picto-column/picto-column.component';
import { ColorColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/color-column/color-column.component';
import { DynamicDocumentFieldInfoType } from '../entities/dynamic-document';
import { DatePipe } from '@angular/common';
import { ParameterLinkComponent } from '../components/dynamic-component-shared/table/dynamic-columns/parameter-link/parameter-link.component';
import { ButtonFieldComponent } from '../components/button-field/button-field.component';
import { LinkbuttonComponent } from '../components/dynamic-component-shared/table/dynamic-columns/linkbutton/linkbutton.component';
import { ResourcesColumnComponent } from '../components/dynamic-component-shared/table/dynamic-columns/resources-column/resources-column.component';

@Injectable({ providedIn: 'root' })
export class TableService {
    constructor(private datePipe: DatePipe) {}

    getColumnConfig(columnDef: any, data: any, languages: Language[]) {
        let fieldConfig: any = {};
        if (!columnDef) {
            return;
        }

        switch (columnDef.type) {
            case 'imagesmap':
                fieldConfig.imagesmap = data;
                fieldConfig.isList = columnDef.list;
                break;

            case 'object':
                fieldConfig.value = data;
                fieldConfig.displayProperty = columnDef.displayProperty;
                fieldConfig.isList = columnDef.list;
                fieldConfig.labelLocal = columnDef.local;
                fieldConfig.linkedEntite = columnDef.entityLink;
                fieldConfig.type = columnDef.type;
                break;
            case 'label':
                fieldConfig.value = data;
                fieldConfig.displayProperty = columnDef.displayProperty;
                fieldConfig.isList = columnDef.list;
                fieldConfig.labelLocal = columnDef.local;
                fieldConfig.linkedEntite = columnDef.entityLink;
                fieldConfig.type = columnDef.type;
                break;

            case DynamicDocumentFieldInfoType.DOCUMENT_LINK:
                fieldConfig.value = data?.labels?.['fr_FR']
                    ? data?.labels?.['fr_FR']
                    : data?.value
                    ? data?.value
                    : data?.documentId
                    ? data.documentId
                    : data;
                break;
            // case DynamicDocumentFieldInfoType.DOCUMENT_LINKS:
            //   fieldConfig.value = data
            //     ?.map((el: any) => (el.documentId ? el.documentId : el))
            //     .join('\n')
            //     .toString();
            //   break;
            case DynamicDocumentFieldInfoType.DOCUMENT_LINKS:
            case DynamicDocumentFieldInfoType.PARAMETER_LINK:
                // For DOCUMENT_LINKS, data is an array, so we should keep it as array
                // Don't try to extract labels or value from array
                if (Array.isArray(data)) {
                    fieldConfig.value = data;
                } else {
                    // If data is not array (shouldn't happen for DOCUMENT_LINKS, but handle it)
                    fieldConfig.value = data?.labels?.['fr_FR']
                        ? data?.labels?.['fr_FR']
                        : data?.value
                        ? data?.value
                        : data;
                }

                break;
            case DynamicDocumentFieldInfoType.PARAMETER_LINKS:
                fieldConfig.value = data
                    ?.map((el: any) =>
                        el?.labels?.['fr_FR']
                            ? el?.labels?.['fr_FR']
                            : el?.value
                            ? el.value
                            : el?.parameterId
                            ? el.parameterId
                            : el
                    )
                    .join('\n')
                    .toString();
                break;
            case DynamicDocumentFieldInfoType.RESOURCE_FILES:
                break;
            case DynamicDocumentFieldInfoType.TEXT:
            case DynamicDocumentFieldInfoType.FORMATTED_TEXT:
            case DynamicDocumentFieldInfoType.MULTILANGUE_FORMATTED_TEXT:
            case 'multilangue':
                fieldConfig.value = data;
                fieldConfig.multilangueMap = data;
                fieldConfig.type = columnDef.type;
                fieldConfig.languages = languages;

                break;

            case DynamicDocumentFieldInfoType.DATE_TIME:
                try {
                    fieldConfig.value = this.datePipe.transform(data, 'dd/MM/yyyy');
                } catch (error) {
                    const monthMap: any = {
                        jan: '01',
                        fev: '02',
                        mars: '03',
                        avr: '04',
                        mai: '05',
                        juin: '06',
                        juil: '07',
                        aout: '08',
                        sept: '09',
                        oct: '10',
                        nov: '11',
                        dec: '12',
                        déc: '12',
                    };

                    // For example : convert 25-oct.-1995 to 25/10/1995
                    let date = data?.split('-');
                    if (date.length === 3) {
                        date[1] = date[1].replace('.', '');
                        if (date[1] in monthMap) {
                            date[1] = monthMap[date[1]];
                        }
                        data = date.join('-');

                        // Reformatage de la date en 'yyyy-MM-dd' pour utilisation avec Date object
                        let formattedDate = `${date[2]}-${date[1]}-${date[0]}`;
                        let dateObject = new Date(formattedDate);

                        if (!isNaN(dateObject.getTime())) {
                            // Transformer la date en format 'dd/MM/yyyy'
                            fieldConfig.value = this.datePipe.transform(dateObject, 'dd/MM/yyyy');
                        } else {
                            console.error('La date transformée est invalide');
                        }
                    } else {
                        // For example : convert Fri Feb 16 07:00:00 CET 2024 to 16/02/2024
                        let date: any = new Date(data);
                        if (isNaN(date)) {
                            const parts = data.match(/\w+ (\w+) (\d+) (\d+:\d+:\d+) CET (\d+)/);
                            if (parts) {
                                date = new Date(`${parts[2]} ${parts[1]} ${parts[4]} ${parts[3]}`);
                            }
                        }
                        fieldConfig.value = this.datePipe.transform(date, 'dd/MM/yyyy');
                    }
                }
                break;

            case 'picto':
            case 'icon':
                fieldConfig.value = data;
                fieldConfig.type = columnDef.type;
                fieldConfig.isList = columnDef.list;
                break;

            case 'color':
                fieldConfig.value = data;
                fieldConfig.isList = columnDef.list;
                fieldConfig.type = columnDef.type;
                fieldConfig.displayProperty = columnDef.displayProperty;
                break;
            case 'actionbutton':
                fieldConfig.type = columnDef.type;
                fieldConfig.label = columnDef.label;
                fieldConfig.clickMethod = columnDef.clickMethod;
                fieldConfig.columnData = data;
                break;
            case 'linkbutton':
                fieldConfig.type = columnDef.type;
                fieldConfig.label = columnDef.label;
                fieldConfig.clickMethod = columnDef.clickMethod;
                fieldConfig.columnData = data;
                break;
        }
        return fieldConfig;
    }

    component: any;
    /***
     * @param
     */

    loadColumnPrimitive(vcf: ViewContainerRef, config: any) {
        this.component = PrimitiveColumnComponent;
        vcf.clear();
        let componentInstance: ComponentRef<typeof this.component> = vcf.createComponent(
            this.component
        );
        componentInstance.setInput('config', config);
        return componentInstance;
    }

    getColumnConfigPrimitive(data: any) {
        let fieldConfig: any = {};
        if (data === null || data === undefined) {
            return;
        }
        fieldConfig.value = data;
        return fieldConfig;
    }

    public async loadColumn(vcf: ViewContainerRef, config: any, columnDefType: string) {
        switch (columnDefType) {
            case 'imagesmap':
                this.component = ImageColumnComponent;
                break;
            case DynamicDocumentFieldInfoType.PARAMETER_LINK:
            case DynamicDocumentFieldInfoType.PARAMETER_LINKS:
            case DynamicDocumentFieldInfoType.DOCUMENT_LINKS:
            case DynamicDocumentFieldInfoType.DOCUMENT_LINK:
                this.component = ParameterLinkComponent;
                break;
            case DynamicDocumentFieldInfoType.RESOURCE_FILES:
                this.component = ResourcesColumnComponent;
                break;
            case 'object':
            case 'label':
                this.component = ObjectColumnComponent;
                break;

            case DynamicDocumentFieldInfoType.TEXT:
            case DynamicDocumentFieldInfoType.FORMATTED_TEXT:
            case DynamicDocumentFieldInfoType.MULTILANGUE_FORMATTED_TEXT:
            case 'multilangue':
                this.component = MultilangueColumnComponent;
                break;
            case 'picto':
            case 'icon':
                this.component = PictoColumnComponent;
                break;
            case 'color':
                this.component = ColorColumnComponent;
                break;
            case 'actionbutton':
                this.component = ButtonFieldComponent;
                break;
            case 'linkbutton':
                this.component = LinkbuttonComponent;
                break;
            default:
                // console.warn("There's no component for column type: " + columnDefType);
                break;
        }

        if (!this.component) {
            return;
        }

        // For DOCUMENT_LINKS: Don't clear and recreate if new config value is empty and existing component has data
        const isDocLinks =
            columnDefType === DynamicDocumentFieldInfoType.DOCUMENT_LINKS ||
            columnDefType === DynamicDocumentFieldInfoType.DOCUMENT_LINK;
        if (isDocLinks && vcf.length > 0) {
            const newConfigValue = config?.value || config?.columnData;
            const newConfigIsEmpty =
                !newConfigValue ||
                (Array.isArray(newConfigValue) && newConfigValue.length === 0) ||
                newConfigValue === null ||
                newConfigValue === undefined;

            if (newConfigIsEmpty) {
                try {
                    const existingComponent = vcf.get(0);
                    if (existingComponent) {
                        const instance = (existingComponent as any)?.instance;
                        const existingConfigValue =
                            instance?.config?.value || instance?.config?.columnData;
                        const existingHasData =
                            Array.isArray(existingConfigValue) && existingConfigValue.length > 0;

                        if (existingHasData) {
                            // Don't clear - keep existing component with data
                            return null; // Return null to indicate we kept the existing component
                        }
                    }
                } catch (e) {
                    // Ignore errors
                }
            }
        }

        vcf.clear();
        const componentClass = this.component;
        let componentInstance: ComponentRef<any> = vcf.createComponent(componentClass);
        componentInstance.setInput('config', config);

        return componentInstance;
    }
}
